import { orderUrls } from '@/api/helper';
import type {
  GetOrderByIdPayloadDto,
  GetOrderByIdResponseDto,
  GetOrdersStatsPayloadDto,
  GetOrdersStatsResponseDto,
  QueryAPI,
  SearchOrderPayloadDto,
  SearchOrderResponseDto,
  UpdateItemsPreparationPayloadDto,
  UpdateItemsPreparationResponseDto,
  UpdateOrderStatusPayloadDto,
  UpdateOrderStatusResponseDto,
} from '@/types/request.dto';

const searchOrder = async (
  payload: SearchOrderPayloadDto,
  queryApi: QueryAPI,
): Promise<SearchOrderResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('authorization') && queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  const searchParams = new URLSearchParams();
  Object.entries(payload).forEach(([key, val]) =>
    val instanceof Array
      ? val.forEach((it) => searchParams.append(key, it))
      : searchParams.append(key, val),
  );

  let data: SearchOrderResponseDto;
  try {
    const response = await fetch(
      `${orderUrls.searchOrders()}?${searchParams.toString()}`,
      {
        method: 'GET',
        signal: signal,
        headers: headers,
      },
    );
    data = await response.json();
  } catch (error) {
    throw new Error('Erreur de connexion');
  }
  return data;
};

const updateOrder = async (
  payload: UpdateOrderStatusPayloadDto,
  queryApi: QueryAPI,
): Promise<UpdateOrderStatusResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('authorization') && queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  const searchParams = new URLSearchParams();
  searchParams.set('orderId', payload.orderId);

  let data: UpdateOrderStatusResponseDto;
  const { orderId, ...body } = payload;

  try {
    const response = await fetch(orderUrls.updateStatus(orderId), {
      method: 'PUT',
      body: JSON.stringify(body),
      signal: signal,
      headers: headers,
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Erreur de connexion');
  }

  return data;
};

const updateItemsPreparation = async (
  payload: UpdateItemsPreparationPayloadDto,
  queryApi: QueryAPI,
): Promise<UpdateItemsPreparationResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('authorization') && queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  const { orderId, ...body } = payload;
  let data: UpdateItemsPreparationResponseDto;

  try {
    const response = await fetch(orderUrls.updateItemsPreparation(orderId), {
      method: 'PUT',
      body: JSON.stringify(body),
      signal: signal,
      headers: headers,
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Erreur de connexion');
  }

  return data;
};

const getOrderById = async (
  payload: GetOrderByIdPayloadDto,
  queryApi: QueryAPI,
): Promise<GetOrderByIdResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('authorization') && queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  let data: GetOrderByIdResponseDto;
  const { orderId, withRemovedItems } = payload;
  const url = orderUrls.getOrderById(orderId);
  if (withRemovedItems) url.searchParams.set('withRemovedItems', 'true');

  try {
    const response = await fetch(url, {
      method: 'GET',
      signal: signal,
      headers: headers,
    });
    data = await response.json();
  } catch (error) {
    throw new Error('Erreur de connexion');
  }

  return data;
};

const getOrderStats = async (
  payload: GetOrdersStatsPayloadDto,
  queryApi: QueryAPI,
): Promise<GetOrdersStatsResponseDto> => {
  const abortCrt = new AbortController();
  const signal = queryApi?.signal ?? abortCrt.signal;

  const headers = new Headers();
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('authorization') && queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  const searchParams = new URLSearchParams();
  Object.entries(payload).forEach(([key, val]) =>
    searchParams.append(key, val),
  );

  let data: GetOrdersStatsResponseDto;

  try {
    const response = await fetch(
      `${orderUrls.getOrderStats()}?${searchParams.toString()}`,
      {
        method: 'GET',
        signal: signal,
        headers: headers,
      },
    );
    data = await response.json();
  } catch (error) {
    throw new Error('Erreur de connexion');
  }

  return data;
};

export interface LineAdjustment {
  orderItemId: string;
  qty: number;
}

const putJson = async <T>(
  url: URL | string,
  body: object | undefined,
  queryApi: QueryAPI,
): Promise<T> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  if (queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  try {
    const response = await fetch(url, {
      method: 'PUT',
      body: body && JSON.stringify(body),
      signal: queryApi.signal,
      headers,
    });
    return await response.json();
  } catch (error) {
    throw new Error('Erreur de connexion');
  }
};

// Le serveur rembourse lui-même la différence d'une commande payée.
const adjustOrderLines = (
  payload: {
    orderId: string;
    adjustments: LineAdjustment[];
    clientMessage?: string;
    reason?: string;
  },
  queryApi: QueryAPI,
) => {
  const { orderId, ...body } = payload;
  return putJson<GetOrderByIdResponseDto>(
    orderUrls.adjustLines(orderId),
    body,
    queryApi,
  );
};

// Aucun argent ne bouge : le serveur déplace l'heure et prévient le client.
const changePickupTime = (
  payload: { orderId: string; pickupTime: string },
  queryApi: QueryAPI,
) => {
  const { orderId, ...body } = payload;
  return putJson<GetOrderByIdResponseDto>(
    orderUrls.changePickupTime(orderId),
    body,
    queryApi,
  );
};

// L'historique d'une commande, du plus ancien au plus récent (le serveur le range ainsi).
const getOrderHistory = async <T>(
  orderId: string,
  queryApi: QueryAPI,
): Promise<{ data?: T[] }> => {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');
  if (queryApi.jwttoken)
    headers.set('authorization', `Bearer ${queryApi.jwttoken}`);

  try {
    const response = await fetch(orderUrls.orderHistory(orderId), {
      method: 'GET',
      signal: queryApi.signal,
      headers,
    });
    return await response.json();
  } catch (error) {
    throw new Error('Erreur de connexion');
  }
};

const abordOrder = (orderId: string, queryApi: QueryAPI) =>
  putJson<UpdateOrderStatusResponseDto>(
    orderUrls.abordOrder(orderId),
    undefined,
    queryApi,
  );

export const orderService = {
  adjustOrderLines,
  getOrderHistory,
  changePickupTime,
  abordOrder,
  searchOrder,
  updateOrder,
  updateItemsPreparation,
  getOrderStats,
  getOrderById,
};
